import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiFillFileAdd } from 'react-icons/ai'
import { MdFilePresent } from 'react-icons/md'
import { RiCloseFill } from 'react-icons/ri'

import AttachFileIcon from '@mui/icons-material/AttachFile'
import { Box, IconButton } from '@mui/material'
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks'

import {
  BG_DARK_GRAY,
  PRIMARY_GREEN,
  PRIMARY_RED,
  PRIMARY_YELLOW,
  PURE_BLACK,
  THEME_GRAY,
} from 'utils/customizedTheme'

interface IProps {
  fieldProps: { name: string; required?: boolean }
  fileData?: { key: string; value: any }[]
  savedAttachmentsKey: string
  values: any
  errors: any
  setFieldValue: any
  disabled?: boolean
  id: string
  formSubmitCount: number,
  saveAttachmentsApiCall?: MutationTrigger<any>
  deleteAttachmentsApiCall?: MutationTrigger<any>
  singleFile?: boolean
}

const AttachmentButton: FC<IProps> = ({
  fieldProps,
  fileData = [],
  id,
  formSubmitCount = 0,
  savedAttachmentsKey,
  values,
  errors,
  setFieldValue,
  disabled,
  saveAttachmentsApiCall,
  deleteAttachmentsApiCall,
  singleFile,
}) => {
  const { t } = useTranslation()
  const addFile = (e: any) => {
    const files = e?.currentTarget?.files || []
    if (files?.length > 0) {
      setFieldValue(fieldProps.name, files)
    }
  }

  const [attachmentIdsForDelete, setAttachmentIdsForDelete] = useState<number[]>([])

  // Upload files after form is saved
  useEffect(() => {
    if (id && formSubmitCount>0 && formSubmitCount<10) {
      if (values?.attachments?.length > 0) {
        const constructedFormData = () => {
          const formData = new FormData()

          formData.append('files', values.attachments)
          if (fileData?.length > 0) {
            fileData.forEach((data) => {
              formData.append(data.key, data.value)
            })
          }
          const files: any = values?.attachments ? Array.from(values.attachments) : []

          for (let i = 0; i < files?.length; i++) {
            formData.append('files', files[i])
          }

          return formData
        }

        saveAttachmentsApiCall({ id, formData: constructedFormData() })
      }

      if (attachmentIdsForDelete.length > 0) {
        attachmentIdsForDelete.forEach((attachmentId) => {
          deleteAttachmentsApiCall({ id, attachmentId })
        })
      }
    }
  }, [id, formSubmitCount])

  const files = values?.attachments ? [...values?.attachments] : []

  return (
    <>
      <Box className="transparent-background"
        sx={{
          '& a:hover': { color: THEME_GRAY + ' !important' },
        }}
      >
        <input
          name={fieldProps.name}
          type='file'
          id='attachments'
          multiple
          style={{ display: 'none' }}
          onChange={(e: any) => addFile(e)}
        />
        <label
          htmlFor='attachments'
          className='hover-blue'
          style={{
            border: '1px solid white',
            borderRadius: '0.8rem',
            padding: '0 0.5rem 0.8rem 1rem',
            position: 'relative',
            cursor: 'pointer',
            display: 'inline-block',
            lineHeight: 1,
            minWidth: '9rem',
            pointerEvents: disabled ? 'none' : 'auto',
            background: disabled ? BG_DARK_GRAY : PRIMARY_YELLOW,
          }}
        >
          <>
            <span
              style={{
                display: 'inline-block',
                lineHeight: '1',
                margin: '0.55rem 0.2rem 0',
                whiteSpace: 'nowrap',
              }}
            >
              {`${t('common.attachments')}`}
              <AttachFileIcon sx={{ margin: '0 0 -0.35rem 0.2rem' }} />
            </span>{' '}
            {fieldProps?.required && (
              <span
                style={{
                  color: PRIMARY_RED,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  position: 'absolute',
                  top: '-0.5rem',
                  right: '-0.8rem',
                }}
              >
                *
              </span>
            )}
          </>
        </label>
        {(files?.length > 0 || values?.[savedAttachmentsKey]?.length > 0) &&
        <span
          style={{
            // fontWeight: 'bold',
            fontSize: '1rem',
            lineHeight: '1.66',
            textAlign: 'left',
            marginTop: '1rem',
            marginLeft: '1rem',
            wordBreak: 'break-all',
          }}
        >
          <ul
            style={{
              listStyle: 'none',
              paddingLeft: '0.5rem',
              marginBottom: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {files?.length > 0 &&
              files.map((file: any, i) => (
                <li
                  key={i}
                  style={{
                    color: PRIMARY_GREEN,
                    minHeight: '2.3rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    wordBreak: 'break-all',
                    flexShrink: 0,
                  }}
                >
                  <>
                    <AiFillFileAdd
                      style={{ margin: '-0.2rem 0.5rem 0 0', fontSize: '1.25rem', color: PRIMARY_GREEN }}
                    />
                    {file.name}
                    <RemoveButton
                      attachmentId={file?.id}
                      disabled={disabled}
                      index={i}
                      itemType='new'
                      values={values}
                      setFieldValue={setFieldValue}
                      savedAttachmentsKey={savedAttachmentsKey}
                      setAttachmentIdsForDelete={setAttachmentIdsForDelete}
                    />
                  </>
                </li>
              ))}
            {values?.[savedAttachmentsKey]?.length > 0 &&
              !singleFile &&
              values[savedAttachmentsKey].map((file: any, index: number) => (
                <li
                  key={index + 'material'}
                  style={{
                    minHeight: '2.3rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  {file.name
                    ? file.name
                    : file.title && (
                        <a
                          href={file.url}
                          target='_blank'
                          rel='noreferrer'
                          style={{ color: PURE_BLACK, textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          <MdFilePresent style={{ margin: '0 0.5rem -0.25rem 0', fontSize: '1.25rem' }} />
                          {file.title}
                          <RemoveButton
                            attachmentId={file?.id}
                            disabled={disabled}
                            index={index}
                            itemType='old'
                            values={values}
                            setFieldValue={setFieldValue}
                            savedAttachmentsKey={savedAttachmentsKey}
                            setAttachmentIdsForDelete={setAttachmentIdsForDelete}
                          />
                        </a>
                      )}
                </li>
              ))}
          </ul>
        </span>
        }
      </Box>
      {errors?.attachments && (
        <p
          style={{
            color: PRIMARY_RED,
            fontSize: '0.75rem',
            lineHeight: '1.66',
            textAlign: 'left',
            marginTop: '0.5rem',
            marginLeft: '1rem',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}
        >
          {errors.attachments as string}
        </p>
      )}
    </>
  )
}
export default AttachmentButton

interface IRemoveProps {
  itemType: 'old' | 'new'
  values: any
  setFieldValue: any
  disabled?: boolean
  index: number
  attachmentId: number
  savedAttachmentsKey: string
  setAttachmentIdsForDelete: React.Dispatch<React.SetStateAction<number[]>>
}

const RemoveButton: FC<IRemoveProps> = ({
  itemType,
  values,
  setFieldValue,
  index,
  disabled = false,
  attachmentId,
  savedAttachmentsKey,
  setAttachmentIdsForDelete,
}) => {
  const handleRemove = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (itemType === 'new') {
      const filteredAttachments = Array.from(values.attachments).filter((f: File, i: number) => i !== index)
      setFieldValue('attachments', filteredAttachments ? filteredAttachments : null)
    }
    if (itemType === 'old') {
      const filteredAttachments = values[savedAttachmentsKey].filter((f: File, i: number) => i !== index)
      setFieldValue(savedAttachmentsKey, filteredAttachments ? filteredAttachments : null)
      setAttachmentIdsForDelete((ids) => [...ids, attachmentId])
    }
  }

  return (
    <>
      {!disabled && (
        <IconButton
          color='error'
          className='remove-btn'
          onClick={handleRemove}
          style={{
            display: 'inline-flex',
            marginLeft: '0.3rem',
            marginTop: '-0.2rem',
            padding: '0.3rem',
          }}
        >
          <RiCloseFill style={{ fontSize: '1.4rem', fontWeight: '700', color: PRIMARY_RED }} />
        </IconButton>
      )}
    </>
  )
}
